import { useState } from 'react';
import toast from 'react-hot-toast';
import { useLogout } from '@/hooks/useLogout';
import { submitAccessRequest, type RequestedRole } from '@/api/user';

const ROLE_OPTIONS: { value: RequestedRole; label: string }[] = [
  { value: 'planner', label: 'Planner (Mindshare)' },
  { value: 'creative_agency', label: 'Creative Agency (VML/Ford)' },
  { value: 'activation', label: 'Activation (Mindshare)' },
  { value: 'approver', label: 'Approver (Ford)' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'asset_manager', label: 'Asset Manager' },
];

export const AccessPendingScreen = () => {
  const logout = useLogout();
  const [selectedRole, setSelectedRole] = useState<RequestedRole | ''>('');
  const [department, setDepartment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRole) {
      toast.error('Please select a role');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await submitAccessRequest({
        requested_role: selectedRole,
        department: department || undefined,
      });

      if (response.success) {
        setIsSubmitted(true);
        toast.success('Access request submitted!');
      } else {
        toast.error(response.message);
      }
    } catch {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        {/* WPP Open Logo */}
        <div className="mb-8">
          <img src="/wpp-open-logo.png" alt="WPP Open" className="h-10 mx-auto" />
        </div>

        {/* Icon */}
        <div className="mb-6">
          <div
            className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${isSubmitted ? 'bg-green-100' : 'bg-amber-100'}`}
          >
            {isSubmitted ? (
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg
                className="w-8 h-8 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-2xl font-bold text-slate-900 mb-3">
          {isSubmitted ? 'Request Submitted' : 'Access Pending'}
        </h1>

        {/* Description */}
        <p className="text-slate-600 mb-6">
          {isSubmitted
            ? 'Your access request has been sent to the administrators. You will receive access once your request is approved.'
            : "You've successfully signed in, but your account hasn't been assigned a role yet. Request access below and an administrator will be notified."}
        </p>

        {!isSubmitted ? (
          /* Access Request Form */
          <form onSubmit={handleSubmit} className="bg-slate-50 rounded-lg p-6 mb-6 text-left">
            <div className="mb-4">
              <label htmlFor="role" className="block text-sm font-medium text-slate-700 mb-2">
                What is your role?
              </label>
              <select
                id="role"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as RequestedRole)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                required
              >
                <option value="">Select your role...</option>
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="department" className="block text-sm font-medium text-slate-700 mb-2">
                Department / Team (optional)
              </label>
              <input
                id="department"
                type="text"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="e.g., Mindshare UK, VML Poland"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-900 placeholder:text-slate-400"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !selectedRole}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Request Access'}
            </button>
          </form>
        ) : (
          /* Success State */
          <div className="bg-green-50 rounded-lg p-6 mb-6">
            <p className="text-green-800 text-sm">
              An administrator will review your request and add you to the system. This usually
              happens within 1 business day.
            </p>
          </div>
        )}

        {/* Logout Button */}
        <button
          onClick={logout}
          className="px-6 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          Sign out and try a different account
        </button>

        {/* VML MAP Attribution */}
        <div className="mt-12">
          <a
            href="https://vml-map.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            <img src="/vml-logo-white.svg" alt="VML MAP" className="h-3 brightness-0 opacity-40" />
            <span>Built by VML MAP</span>
          </a>
        </div>
      </div>
    </div>
  );
};
